import React, { ReactElement, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { BreadcrumbItemProps } from '../../../../shared/components/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import Breadcrumbs from '../../../../shared/components/Breadcrumbs/Breadcrumbs';
import CompanySelector from '../../../../shared/components/CompanySelector/CompanySelector';
import HoldingSelector from '../../../../shared/components/HoldingSelector/HoldingSelector';
import PageCurvedContainer from '../../../../shared/components/PageCurvedContainer/PageCurvedContainer';
import PageHeader from '../../../../shared/components/PageHeader/PageHeader';
import StyledButton from '../../../../shared/components/StyledButton/StyledButton';
import StyledMenu, { IStyledMenuListProps } from '../../../../shared/components/StyledMenu/StyledMenu';
import StyledTabBar from '../../../../shared/components/StyledTabBar/StyledTabBar';
import Header from '../../../../shared/components/v3/Globals/Header/Header';
import { INTEGRATIONS_ROUTE } from '../../../../shared/constants/auth';
import { MENU_DIRECTION_RIGHT, TABLET_SCREEN_SIZE } from '../../../../shared/constants/constants';
import { ARROW_DOWN_IMAGES } from '../../../../shared/constants/images';
import { SETTINGS_OPTION_MENU_NE } from '../../../../shared/constants/settings';
import { CAN_SEE_INTEGRATIONS_FG } from '../../../../shared/constants/statsig';
import {
  CLICK_ACTION,
  CONFIGURATION_SECTION,
  SECTION_CONFIGURATION,
  SETTINGS_TABS_DICT
} from '../../../../shared/constants/tagManager';
import usePermissions from '../../../../shared/hooks/usePermissions/usePermissions';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_SETTINGS } from '../../../../shared/translations/settings';

import './SettingsPageLayoutNE.scss';

const styledMenuListProps: IStyledMenuListProps = {
  width: TABLET_SCREEN_SIZE
};

export interface SettingsPageLayoutNEProps {
  children: ReactElement<any, any> | ReactElement<any, any>[];
}

const SettingsPageLayoutNE = ({ children }: SettingsPageLayoutNEProps) => {
  const [anchorElCollabsOptions, setAnchorElCollabsOptions] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyAccess } = usePermissions();
  const { client } = useStatsigClient();

  const getOptionsByRole = () => {
    const settingsOptionMenu = SETTINGS_OPTION_MENU_NE;

    return settingsOptionMenu
      .filter((option) => {
        if (option.pathName === INTEGRATIONS_ROUTE && !client?.checkGate(CAN_SEE_INTEGRATIONS_FG)) {
          return false;
        }
        return hasAnyAccess(option.permissionsRequired);
      })
      .map((option) => ({
        ...option,
        label: t(option.label)
      }));
  };

  const getOptionsWithActions = getOptionsByRole().map((item) => {
    const optionAction = () => {
      navigate(item.pathName);
    };
    return { ...item, action: optionAction };
  });

  const getLabelTexts = getOptionsByRole().map((option) => option.label);

  const breadcrumbs: BreadcrumbItemProps[] = [{ label: t(TRANSLATIONS_SETTINGS.title) }];

  const getCurrentOptionIndex = () => {
    const pathName = location.pathname;
    const options = getOptionsByRole();
    for (let index = 0; index < options.length; index++) {
      const option = options[index];
      if (pathName === option.pathName) {
        return index;
      }
    }
    return 0;
  };

  const getCurrentOption = () => {
    const currentOptionIndex = getCurrentOptionIndex();
    return getOptionsByRole()[currentOptionIndex];
  };

  const changeView = (newValue: number) => {
    const option = getOptionsByRole()[newValue];
    const gtmParams = {
      section: SECTION_CONFIGURATION,
      subsection: SETTINGS_TABS_DICT[option.id].subsection
    };
    TagManagerService.pushEvent(CLICK_ACTION, CONFIGURATION_SECTION, gtmParams);
    navigate(option.pathName);
  };

  const tabBarParams = {
    items: getLabelTexts,
    action: changeView,
    value: getCurrentOptionIndex()
  };

  const handleCloseCollabsOptions = () => {
    setAnchorElCollabsOptions(null);
  };

  const handleCollaboratorsOptionsClick = (event: any) => {
    setAnchorElCollabsOptions(event.currentTarget);
  };

  return (
    <div className="page-container curved">
      <Header />
      <div className="page-content-container">
        <div className="settings-header-container">
          <div className="settings-company-selector">
            <CompanySelector menuDirection={MENU_DIRECTION_RIGHT} />
          </div>
          <div className="settings-holding-selector">
            <HoldingSelector menuDirection={MENU_DIRECTION_RIGHT} />
          </div>
        </div>
        <Breadcrumbs items={breadcrumbs} />
        <PageHeader
          title={t(TRANSLATIONS_SETTINGS.pageLayout.title)}
          description={t(TRANSLATIONS_SETTINGS.pageLayout.subtitle)}
        />
      </div>
      <PageCurvedContainer>
        {tabBarParams?.items.length > 1 && (
          <>
            <div className="settings-tab-ne">
              <StyledTabBar items={tabBarParams.items} action={tabBarParams.action} activeIndex={tabBarParams.value} />
            </div>
            <div className="settings-dropdown-container-ne">
              <StyledButton buttonType="secondary" action={handleCollaboratorsOptionsClick} image={ARROW_DOWN_IMAGES}>
                {getCurrentOption().label}
              </StyledButton>
            </div>
            <StyledMenu
              anchorEl={anchorElCollabsOptions}
              handleClose={handleCloseCollabsOptions}
              menuItems={getOptionsWithActions}
              styledMenuListProps={styledMenuListProps}
            />
          </>
        )}
        {children}
      </PageCurvedContainer>
    </div>
  );
};

export default SettingsPageLayoutNE;
